/**
 * Helpers for /getroomstatuses `reservations_array` rows on the room monitor.
 * Many PMS payloads omit `room_number` when unassigned rather than sending null.
 */

import moment from 'moment';

/** PMS / JSON often sends today flags as 1/0 or "1"/"0" — align with room-level checks (truthy). */
export function isTruthyMonitorTodayFlag(v) {
    if (v === true || v === 1) return true;
    if (v === false || v === 0 || v == null) return false;
    const s = String(v).trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'yes';
}

/** Flatten optional nested `reservation` payload seen in some PMS responses */
export function reservationRowForDates(r) {
    if (!r || typeof r !== 'object') return r;
    const inner = r.reservation;
    return inner && typeof inner === 'object' ? { ...inner, ...r } : r;
}

/**
 * True if this reservations_array row is a check-in today for A&D, even when the outer row
 * shadows nested `reservation` with falsey defaults, or when there is no confirmation_number.
 */
export function reservationHasCheckinTodayForMonitor(r) {
    const row = reservationRowForDates(r);
    if (isTruthyMonitorTodayFlag(row.checkin_today) || isTruthyMonitorTodayFlag(row.check_in_today)) {
        return true;
    }
    const inner = r?.reservation;
    if (inner && typeof inner === 'object') {
        if (isTruthyMonitorTodayFlag(inner.checkin_today) || isTruthyMonitorTodayFlag(inner.check_in_today)) {
            return true;
        }
    }
    return false;
}

/** Same as {@link reservationHasCheckinTodayForMonitor} for departure today. */
export function reservationHasCheckoutTodayForMonitor(r) {
    const row = reservationRowForDates(r);
    if (isTruthyMonitorTodayFlag(row.checkout_today) || isTruthyMonitorTodayFlag(row.check_out_today)) {
        return true;
    }
    const inner = r?.reservation;
    if (inner && typeof inner === 'object') {
        if (isTruthyMonitorTodayFlag(inner.checkout_today) || isTruthyMonitorTodayFlag(inner.check_out_today)) {
            return true;
        }
    }
    return false;
}

/** Confirmation or reservations_id — enough to show badges / A&D column */
export function hasReservationListIdentity(r) {
    const row = reservationRowForDates(r);
    const confirmation = row?.confirmation_number;
    const reservationId = row?.reservations_id;
    const hasConfirmation = confirmation != null && String(confirmation).trim() !== '';
    const hasReservationId = reservationId != null && String(reservationId).trim() !== '';
    return hasConfirmation || hasReservationId;
}

/**
 * Normalized assigned room (non-empty means guest is assigned to a room).
 */
export function getReservationAssignedRoomNormalized(r) {
    const row = reservationRowForDates(r);
    const candidates = [
        row?.room_number,
        row?.room_no,
        row?.assigned_room_number,
        row?.assigned_room,
        row?.reservation_room_number
    ];
    for (const raw of candidates) {
        if (raw == null || raw === '') continue;
        if (typeof raw === 'number' && raw === 0) continue;
        const s = String(raw).trim();
        if (s === '' || s === '0') continue;
        const lower = s.toLowerCase();
        if (lower === 'null' || lower === 'undefined' || lower === 'n/a') continue;
        return s;
    }
    const pmsRoom = row?.pms_rooms_id;
    if (pmsRoom != null && String(pmsRoom).trim() !== '' && String(pmsRoom).trim() !== '0') {
        return String(pmsRoom).trim();
    }
    return null;
}

export function isReservationUnassignedForMonitor(r) {
    return getReservationAssignedRoomNormalized(r) == null;
}

/**
 * True if this physical room row from /getroomstatuses already has a guest/reservation on the door.
 * Use for "Show Matched Room Types Only" so only empty rooms are listed; room-level data must be counted
 * in addition to reservations_array room assignments.
 */
export function roomHasReservationAttached(room, reservationsArray) {
    if (!room || typeof room !== 'object') return false;
    if (Array.isArray(room.confirmation_numbers) && room.confirmation_numbers.length > 0) {
        return room.confirmation_numbers.some((c) => hasReservationListIdentity(c));
    }
    if (room.confirmation_number != null && String(room.confirmation_number).trim() !== '') {
        return true;
    }
    if (!Array.isArray(reservationsArray)) return false;
    const roomNumber = room.roomNumber ?? room.room_number;
    if (roomNumber == null || String(roomNumber).trim() === '') return false;
    return reservationsArray.some((reservation) => {
        const assignedRoom = getReservationAssignedRoomNormalized(reservation);
        return assignedRoom && String(assignedRoom) === String(roomNumber);
    });
}

/**
 * reservations_array rows vary by PMS; try common arrival/departure field names.
 */
export function getReservationArrivalMoment(r) {
    const row = reservationRowForDates(r);
    const keys = [
        'arrival_date_time',
        'arrival_date',
        'checkin_date',
        'check_in_date',
        'start_date',
        'date_from',
        'arrival',
        'arrival_dt',
        'scheduled_arrival',
        'book_arrival_date',
        'expected_arrival',
        'arrive_date',
        'reservation_date'
    ];
    for (const key of keys) {
        const raw = row?.[key];
        if (raw == null || String(raw).trim() === '') continue;
        const m = moment(raw);
        if (m.isValid()) return m;
    }
    return null;
}

export function getReservationDepartureMoment(r) {
    const row = reservationRowForDates(r);
    const keys = [
        'departure_date_time',
        'departure_date',
        'checkout_date',
        'check_out_date',
        'end_date',
        'date_to',
        'departure',
        'departure_dt',
        'scheduled_departure',
        'book_departure_date',
        'expected_departure',
        'depart_date'
    ];
    for (const key of keys) {
        const raw = row?.[key];
        if (raw == null || String(raw).trim() === '') continue;
        const m = moment(raw);
        if (m.isValid()) return m;
    }
    return null;
}

function monitorConfirmationEntryKey(entry) {
    if (!entry || typeof entry !== 'object') return null;
    const confirmation = entry.confirmation_number;
    if (confirmation != null && String(confirmation).trim() !== '') {
        return `c:${String(confirmation).trim()}`;
    }
    const reservationId = entry.reservations_id;
    if (reservationId != null && String(reservationId).trim() !== '') {
        return `r:${String(reservationId).trim()}`;
    }
    return null;
}

function mergeMonitorConfirmationEntries(existing, incoming) {
    return {
        ...existing,
        ...incoming,
        checkin_today: !!(existing.checkin_today || incoming.checkin_today),
        checkout_today: !!(existing.checkout_today || incoming.checkout_today),
        confirmation_number:
            existing.confirmation_number != null && String(existing.confirmation_number).trim() !== ''
                ? existing.confirmation_number
                : incoming.confirmation_number,
        reservations_id: existing.reservations_id ?? incoming.reservations_id,
        guest_first:
            existing.guest_first != null && String(existing.guest_first).trim() !== ''
                ? existing.guest_first
                : incoming.guest_first,
        guest_last:
            existing.guest_last != null && String(existing.guest_last).trim() !== ''
                ? existing.guest_last
                : incoming.guest_last
    };
}

function roomLevelConfirmationEntries(room) {
    if (Array.isArray(room?.confirmation_numbers) && room.confirmation_numbers.length > 0) {
        return room.confirmation_numbers.filter((c) => c && typeof c === 'object');
    }
    if (room?.confirmation_number != null && String(room.confirmation_number).trim() !== '') {
        return [
            {
                confirmation_number: room.confirmation_number,
                reservations_id: room.reservations_id,
                checkout_today: isTruthyMonitorTodayFlag(room.checkout_today) ||
                    isTruthyMonitorTodayFlag(room.check_out_today),
                checkin_today: isTruthyMonitorTodayFlag(room.checkin_today) ||
                    isTruthyMonitorTodayFlag(room.check_in_today),
                ...(room.guest_last != null && room.guest_last !== '' ? { guest_last: room.guest_last } : {}),
                ...(room.guest_first != null && String(room.guest_first).trim() !== ''
                    ? { guest_first: room.guest_first }
                    : {})
            }
        ];
    }
    return [];
}

/**
 * Build one `confirmation_numbers` badge row from a reservations_array entry.
 * Uses API today flags when present; otherwise compares arrival/departure to monitor show date.
 */
export function reservationToMonitorConfirmationEntry(reservation, showDateYmd) {
    if (!hasReservationListIdentity(reservation)) return null;
    const row = reservationRowForDates(reservation);
    const showDay = moment(showDateYmd || undefined).startOf('day');
    const arrival = getReservationArrivalMoment(reservation);
    const departure = getReservationDepartureMoment(reservation);

    const checkin_today =
        reservationHasCheckinTodayForMonitor(reservation) ||
        (!!arrival && arrival.clone().startOf('day').isSame(showDay, 'day'));
    const checkout_today =
        reservationHasCheckoutTodayForMonitor(reservation) ||
        (!!departure && departure.clone().startOf('day').isSame(showDay, 'day'));

    const entry = {
        confirmation_number:
            row.confirmation_number != null && String(row.confirmation_number).trim() !== ''
                ? String(row.confirmation_number)
                : undefined,
        reservations_id: row.reservations_id,
        checkin_today,
        checkout_today
    };
    if (row.guest_last != null && row.guest_last !== '') {
        entry.guest_last = row.guest_last;
    }
    if (row.guest_first != null && String(row.guest_first).trim() !== '') {
        entry.guest_first = row.guest_first;
    }
    return entry;
}

/**
 * Merge assigned reservations_array rows into each room's confirmation_numbers so turnover
 * rooms (departure + arrival same day) render both badges when rooms_array only carries one guest.
 */
export function enrichMonitorRoomsWithAssignedReservations(floors, reservationsArray, showDateYmd) {
    if (!Array.isArray(floors) || floors.length === 0) return floors;
    if (!Array.isArray(reservationsArray) || reservationsArray.length === 0) return floors;

    const reservationsByRoom = {};
    for (const reservation of reservationsArray) {
        const roomNumber = getReservationAssignedRoomNormalized(reservation);
        if (!roomNumber) continue;
        const entry = reservationToMonitorConfirmationEntry(reservation, showDateYmd);
        if (!entry) continue;
        const key = String(roomNumber);
        if (!reservationsByRoom[key]) reservationsByRoom[key] = [];
        reservationsByRoom[key].push(entry);
    }

    return floors.map((floor) => ({
        ...floor,
        rooms: floor.rooms.map((room) => {
            const roomNumber = String(room.roomNumber ?? room.room_number ?? '');
            const reservationEntries = reservationsByRoom[roomNumber];
            if (!reservationEntries?.length) return room;

            const mergedByKey = new Map();
            for (const entry of roomLevelConfirmationEntries(room)) {
                const key = monitorConfirmationEntryKey(entry);
                if (key) mergedByKey.set(key, entry);
            }
            for (const entry of reservationEntries) {
                const key = monitorConfirmationEntryKey(entry);
                if (!key) continue;
                const existing = mergedByKey.get(key);
                mergedByKey.set(
                    key,
                    existing ? mergeMonitorConfirmationEntries(existing, entry) : entry
                );
            }

            const confirmation_numbers = Array.from(mergedByKey.values());
            if (confirmation_numbers.length === 0) return room;

            const hasCheckoutToday = confirmation_numbers.some((c) => c.checkout_today);
            const hasCheckinToday = confirmation_numbers.some((c) => c.checkin_today);

            return {
                ...room,
                confirmation_numbers,
                checkout_today: hasCheckoutToday || isTruthyMonitorTodayFlag(room.checkout_today),
                checkin_today: hasCheckinToday || isTruthyMonitorTodayFlag(room.checkin_today)
            };
        })
    }));
}
